/**
 * PDFUploadArea.js
 *
 * Upload area component for attaching a PDF to a template version.
 * Shown when no PDF is yet attached (empty-state UI).
 *
 * Features:
 * - Dropzone appearance with dashed border
 * - Upload button and drag-drop instructions
 * - Minimal styling focused on clarity
 *
 * Props:
 * - onUpload: callback (file) when user selects file (not yet implemented)
 */

import React, { useRef } from 'react';
import { COLORS, TYPOGRAPHY, SPACING, RADIUS } from '../designSystem';

export default function PDFUploadArea({ onUpload }) {
  const fileInputRef = useRef(null);

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file && file.type === 'application/pdf') {
      onUpload?.(file);
    } else if (file) {
      alert('Please select a PDF file');
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.dropzone}>
        <div style={styles.iconWrapper}>
          <svg
            width="48"
            height="48"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            style={{ color: COLORS.PRIMARY_DARK }}
          >
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="17 8 12 3 7 8" />
            <line x1="12" y1="3" x2="12" y2="15" />
          </svg>
        </div>

        <h3 style={styles.title}>Upload PDF</h3>
        <p style={styles.subtitle}>
          Upload your PDF to begin field authoring
        </p>

        <button onClick={handleClick} style={styles.uploadButton}>
          Choose PDF File
        </button>

        <p style={styles.hint}>or drag and drop a PDF file here</p>

        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf,application/pdf"
          onChange={handleFileChange}
          style={{ display: 'none' }}
        />
      </div>

      <div style={styles.infoBox}>
        <h4 style={styles.infoTitle}>Supported Format</h4>
        <ul style={styles.infoList}>
          <li>PDF files only</li>
          <li>Any size supported</li>
          <li>Single or multi-page</li>
        </ul>
      </div>
    </div>
  );
}

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: SPACING.LG,
    padding: SPACING.LG,
    height: '100%',
    justifyContent: 'flex-start',
  },

  dropzone: {
    border: `2px dashed ${COLORS.BORDER_DEFAULT}`,
    borderRadius: RADIUS.DEFAULT,
    padding: SPACING.XXXL,
    textAlign: 'center',
    backgroundColor: COLORS.BG_SURFACE_SECONDARY,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: SPACING.MD,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },

  iconWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '64px',
    width: '64px',
  },

  title: {
    ...TYPOGRAPHY.H4,
    color: COLORS.TEXT_PRIMARY,
    margin: 0,
    marginTop: SPACING.SM,
  },

  subtitle: {
    ...TYPOGRAPHY.BODY,
    color: COLORS.TEXT_LIGHT,
    margin: 0,
  },

  uploadButton: {
    padding: `${SPACING.SM} ${SPACING.LG}`,
    backgroundColor: COLORS.PRIMARY_DARK,
    color: COLORS.TEXT_WHITE,
    border: 'none',
    borderRadius: RADIUS.DEFAULT,
    fontSize: TYPOGRAPHY.BUTTON.fontSize,
    fontWeight: TYPOGRAPHY.BUTTON.fontWeight,
    fontFamily: TYPOGRAPHY.FONT_FAMILY_BODY,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    marginTop: SPACING.SM,
  },

  hint: {
    ...TYPOGRAPHY.BODY_SM,
    color: COLORS.TEXT_LIGHT,
    margin: 0,
    fontStyle: 'italic',
  },

  infoBox: {
    border: `1px solid ${COLORS.BORDER_LIGHT}`,
    borderRadius: RADIUS.DEFAULT,
    padding: SPACING.MD,
    backgroundColor: COLORS.BG_SURFACE_PRIMARY,
  },

  infoTitle: {
    ...TYPOGRAPHY.H5,
    color: COLORS.TEXT_PRIMARY,
    margin: 0,
    marginBottom: SPACING.SM,
  },

  infoList: {
    ...TYPOGRAPHY.BODY_SM,
    color: COLORS.TEXT_LIGHT,
    margin: 0,
    paddingLeft: SPACING.LG,
    lineHeight: '1.8',
  },
};
